import { Input } from "@/components/ui/input";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { FormLabel } from "@/components/ui/form";
import { INPUT_FIELD_CLASSNAME } from "@/constants/classname/classname";
import { WorkflowStepType } from "@/constants/workflows/workflowCreate";
import { RootState } from "@/redux";
import { AppField } from "@/types/WorkflowCreate/fields";
import { cn } from "@/lib/utils";
import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, Combine } from "lucide-react";
import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

// Think about more comprehensive decoding if needed
const decodeBasicHtmlEntities = (value: string) => {
  const entityMap = {
    "&gt;": ">",
    "&lt;": "<",
    "&quot;": '"',
    "&#39;": "'",
  } as const;

  // Decode "&amp;" last so values like "&amp;gt;" become "&gt;" (not ">") in a single pass.
  const partiallyDecoded = value.replace(
    /&gt;|&lt;|&quot;|&#39;/g,
    (match) => entityMap[match as keyof typeof entityMap]
  );
  return partiallyDecoded.replace(/&amp;/g, "&");
};

export const NestedDropdown = ({
  data,
  onSelect,
  field,
  fieldSubtitle,
  label,
  description,
  required,
  showErrorFields,
  initialSelectedLabel,
  showCustomInput,
  customInputValue,
  customInputPlaceholder,
  onCustomInputChange,
  customInputError,
}: {
  data: AppField[];
  onSelect: (item: AppField) => void;
  field: string;
  fieldSubtitle?: string;
  label?: string;
  description?: string;
  required?: boolean,
  showErrorFields?: boolean,
  initialSelectedLabel?: string,
  showCustomInput?: boolean,
  customInputValue?: string,
  customInputPlaceholder?: string,
  onCustomInputChange?: (value: string) => void,
  customInputError?: boolean,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string>(initialSelectedLabel || "");

  useEffect(() => {
    setSelectedLabel(initialSelectedLabel || "");
  }, [initialSelectedLabel]);

  const displayLabel = useMemo(() => {
    const rawLabel = showCustomInput && customInputValue?.trim()?.length
      ? customInputValue
      : selectedLabel;

    return rawLabel ? decodeBasicHtmlEntities(rawLabel) : rawLabel;
  }, [customInputValue, selectedLabel, showCustomInput]);

  const handleSelect = (item: AppField) => {
    setSelectedLabel(item.displayName);
    onSelect(item);
    setOpen(false); // close all dropdowns
  };

  const preventTriggerToggle = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  const triggerClassName = cn(
    INPUT_FIELD_CLASSNAME,
    ` ${!field && showErrorFields ? "!border-destructive !bg-destructive-foreground" : ""}`,
    "bg-foreground flex flex-col items-start justify-center leading-4 truncate whitespace-nowrap overflow-hidden text-ellipsis max-w-[280px]"
  );

  return (
    <div className="nestedDropdown">
      {label && (
        <FormLabel
          className="flex flex-col items-start justify-center font-medium text-left text-primary !text-sm cursor-pointer"
          isRequired={required}
        >
          <span>{label}</span>
        </FormLabel>
      )}
      {description && (
        <p className="text-secondary text-sm font-normal -mt-1 mb-1">{description}</p>
      )}
      <RadixDropdownMenu.Root open={open} onOpenChange={setOpen}>
        <RadixDropdownMenu.Trigger asChild>
          {showCustomInput ? (
            <div
              className={cn(triggerClassName, "h-auto py-2 space-y-2 cursor-pointer")}
              role="button"
              tabIndex={0}
            >
              <div className="flex flex-col items-start justify-center w-full">
                {displayLabel ? (
                  <span className="text-left text-sm font-medium flex flex-col items-start justify-center">
                    {displayLabel}
                    <span className="text-secondary text-xs font-normal">{fieldSubtitle || field}</span>
                  </span>
                ) : (
                  <span className="flex flex-row items-center justify-between w-full">
                    <span className="text-sm">Select</span>
                    <ChevronDown className="inline-block ml-2 mb-1 h-4 w-4 text-secondary" />
                  </span>
                )}
              </div>
              <Input
                value={customInputValue}
                placeholder={customInputPlaceholder}
                onClick={preventTriggerToggle}
                onKeyDown={preventTriggerToggle}
                onPointerDown={preventTriggerToggle}
                onChange={(event) => {
                  preventTriggerToggle(event);
                  onCustomInputChange?.(event.currentTarget.value);
                }}
                className={cn(
                  "bg-background text-primary text-sm h-8 w-full",
                  customInputError && "!border-destructive !bg-destructive-foreground/20"
                )}
              />
            </div>
          ) : (
            <button
              className={cn(triggerClassName, "h-[40px]")}
            >
              {displayLabel ? <span className="text-left text-sm font-medium flex flex-col items-start justify-center">
                {displayLabel}
                <span className="text-secondary text-xs font-normal">{fieldSubtitle || field}</span>
              </span> : <span className="flex flex-row items-center justify-between w-full">
                <span className="text-sm">Select</span>
                <ChevronDown className="inline-block ml-2 mb-1 h-4 w-4 text-secondary" />
              </span>}
            </button>
          )}
        </RadixDropdownMenu.Trigger>
        <RadixDropdownMenu.Content
          sideOffset={5}
          className="bg-foreground border border-1 rounded-md DropdownMenuContent w-[280px]"
        >
          <DropdownLevel data={data} onSelect={handleSelect} />
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Root>
    </div>
  );
};

const DropdownLevel = ({
  data,
  onSelect,
}: {
  data: AppField[];
  onSelect: (item: AppField) => void;
}) => {
  const { applications } = useSelector((state: RootState) => state.application);
  const [query, setQuery] = useState("");

  const applicationByName = useMemo(() => {
    const map = new Map<string, { name: string; logo?: string }>();
    for (const app of applications) {
      map.set(app.name, app);
    }
    return map;
  }, [applications]);

  const getDecodedDisplayName = (item: AppField) => decodeBasicHtmlEntities(item.displayName);

  const filtered = data.filter((item) =>
    getDecodedDisplayName(item).toLowerCase().includes(query.toLowerCase())
  );

  return (
    <>
      <div className="p-2 border-b border-muted sticky top-0 bg-foreground z-10">
        <input
          type="text"
          placeholder="Search fields..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="w-full bg-background text-primary px-2 py-1 rounded-md text-sm outline-none"
        />
      </div>
      <div className="max-h-[300px] overflow-y-auto thin-scrollbar">
        {filtered.length ? (
          filtered.map((item, i) =>
            item.fields?.length ? (
              <DropdownMenuSub key={item.codeChain + i}>
                <DropdownMenuSubTrigger className="font-medium">
                  <div className="flex flex-row items-center">
                    {applicationByName.get(item.codeChain) && (
                      <img
                        src={applicationByName.get(item.codeChain)?.logo}
                        alt={getDecodedDisplayName(item)}
                        className="mr-2 h-4 max-w-8"
                      />
                    )}
                    {item.codeChain === WorkflowStepType.TRANSFORMATION && (<Combine className="mr-2 h-4 w-4" />)}
                    <div className="flex flex-col items-start justify-center">
                      {getDecodedDisplayName(item)}
                      {item?.description?.length ? (
                        <span className="text-secondary -mt-1 font-normal">
                          {item.description}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="bg-foreground text-primary w-[280px]">
                  <DropdownLevel data={item.fields} onSelect={onSelect} />
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ) : (
              <DropdownMenuItem
                key={item.codeChain + i}
                onSelect={(e) => {
                  e.preventDefault();
                  onSelect(item);
                }}
                className="cursor-pointer hover:!text-foreground flex flex-col items-start justify-start"
              >
                <span className="font-medium">{getDecodedDisplayName(item)}</span>
                {item?.description?.length ? (
                  <span className="text-secondary -mt-3">{item.description}</span>
                ) : null}
              </DropdownMenuItem>
            )
          )
        ) : (
          <div className="p-2 text-secondary text-sm">No results found</div>
        )}
      </div>
    </>
  );
};
